import { isWeb } from '@zonofi/common';
import { Api } from '../api';
import {
  ListFilterKeys,
  ListInputItemOptions,
  Mappings,
  MobileSiftScreen,
} from '../filter/model';
import {
  addListInputItems,
  updateMobileFiltersByScreen,
} from '../filter/slice';
import { createListFiltersData } from '../filter/helpers';
import {
  CreateBuyXGetYRequest,
  Scheme,
  Schemes,
  SchemesRequest,
  CreateSchemeDiscountRequest,
  CreateTrailSchemeResponse,
  CreateTrailSchemeRequest,
  UpdateTurnoverInvoiceStatusRequest,
  UpdateTurnoverInvoiceStatusResponse,
  BulkUpdateTurnoverInvoiceStatusRequest,
  BulkUpdateTurnoverInvoiceStatusResponse,
  UpdateTurnoverSchemeSortOrderRequest,
  UpdateTurnoverSchemeSortOrderResponse,
  GetTurnoverInvoiceActivityLogsRequest,
  TurnoverInvoiceActivityLogResponse,
  EvaluateTurnoverInvoiceResponse,
  EvaluateTurnoverInvoiceRequest,
  RemapCustomerProductRequest,
  RemapCustomerProductResponse,
  BulkCreateBuyXGetYSchemeRequest,
  BulkCreateBuyXGetYSchemeResponse,
  BulkCreateProductDiscountSchemeRespone,
  BulkCreateProductDiscountSchemeRequest,
  SchemesFiltersResponse,
} from './model';

export const schemesApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getAllSchemes: builder.mutation<
      Schemes,
      {
        principalWorkspaceId: string;
        params?: SchemesRequest;
      }
    >({
      query(arg) {
        const { params } = arg;
        const { body, ...restParams } = params || {};
        return {
          url: `hub/commerce-v2/scheme/${arg.principalWorkspaceId}?sellerWorkspaceId=${arg.principalWorkspaceId}`,
          method: 'POST',
          params: restParams,
          body: body,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        if (request.params?.dispatchFilters) {
          dispatch(
            updateMobileFiltersByScreen({
              screen: MobileSiftScreen.SCHEMES,
              filter: {},
              loading: false,
            })
          );
        }
        queryFulfilled
          .then((response) => {
            const { data } = response;
            if (request?.params?.dispatchFilters) {
              if (!isWeb()) {
                const mapping: Mappings<Schemes>[] = [
                  {
                    dataKey: 'cfa',
                    idKey: 'cfaId',
                    valueKey: 'supplierName',
                    filterKey: ListFilterKeys.Cfa,
                  },
                  {
                    dataKey: 'division',
                    idKey: 'divisionId',
                    valueKey: 'name',
                    filterKey: ListFilterKeys.Divisions,
                  },
                ];
                const listFilter = createListFiltersData(data, mapping);
                dispatch(
                  updateMobileFiltersByScreen({
                    screen: MobileSiftScreen.SCHEMES,
                    filter: { listFilter },
                    loading: false,
                  })
                );
              }
            }
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() =>
            dispatch(
              updateMobileFiltersByScreen({
                screen: MobileSiftScreen.SCHEMES,
                filter: {},
                loading: false,
              })
            )
          );
      },
    }),

    getSchemeFilters: builder.query<
      SchemesFiltersResponse,
      {
        sellerWorkspaceId: string;
      }
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/filters/${arg.sellerWorkspaceId}?sellerWorkspaceId=${arg.sellerWorkspaceId}`,
          method: 'GET',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;

            const cfaInputItems = data?.cfas?.map((x) => {
              return {
                id: x.id,
                value: x.name,
                selected: false,
              };
            });
            dispatch(
              addListInputItems({
                inputItems: cfaInputItems,
                key: ListInputItemOptions.cfa,
              })
            );

            const divisionInputItems = data?.divisions?.map((x) => {
              return {
                id: x?.id,
                value: x.name,
                selected: false,
              };
            });

            dispatch(
              addListInputItems({
                inputItems: divisionInputItems,
                key: ListInputItemOptions.divisions,
              })
            );

            const categoriesInputItems = data?.categories?.map((x) => {
              return {
                id: x?.id,
                value: x.name,
                selected: false,
              };
            });

            dispatch(
              addListInputItems({
                inputItems: categoriesInputItems,
                key: ListInputItemOptions.categories,
              })
            );

            const customerInputItems = data?.customers?.map((x) => {
              return {
                id: x?.id,
                value: x.name,
                selected: false,
              };
            });
            dispatch(
              addListInputItems({
                inputItems: customerInputItems,
                key: ListInputItemOptions.customers,
              })
            );
          })
          .catch((error) => {
            console.log(error);
          });
      },
    }),

    getScheme: builder.query<
      Scheme,
      { principalWorkspaceId: string; id: number }
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/${arg.principalWorkspaceId}/${arg.id}?sellerWorkspaceId=${arg.principalWorkspaceId}`,
          method: 'GET',
        };
      },
    }),
    createBuyXGetY: builder.mutation<Scheme, CreateBuyXGetYRequest>({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/v2/buyXgetYfree/${arg.workspaceId}?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'POST',
          body: arg,
        };
      },
    }),
    createProductDiscount: builder.mutation<
      Scheme,
      CreateSchemeDiscountRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/v2/productdiscount/${arg?.workspaceId}?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'POST',
          body: arg,
        };
      },
    }),
    updateProductScheme: builder.mutation<any, any>({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/v2/update/${arg?.workspaceId}?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'POST',
          body: arg?.payload,
        };
      },
    }),
    createTrailScheme: builder.mutation<
      CreateTrailSchemeResponse,
      CreateTrailSchemeRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/scheme/productdiscount/trial/${arg.workspaceId}?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'POST',
        };
      },
    }),
    updateTurnoverInvoiceStatus: builder.mutation<
      UpdateTurnoverInvoiceStatusResponse,
      UpdateTurnoverInvoiceStatusRequest
    >({
      query(arg) {
        const { invoiceId, sellerWorkspaceId, status, remarks } = arg;
        return {
          url: `hub/proxy/api/turnover-invoices/${invoiceId}/update-status`,
          method: 'PUT',
          body: {
            status,
            remarks,
          },
          params: {
            sellerWorkspaceId,
          },
        };
      },
    }),
    bulkUpdateTurnoverInvoiceStatus: builder.mutation<
      BulkUpdateTurnoverInvoiceStatusResponse,
      BulkUpdateTurnoverInvoiceStatusRequest
    >({
      query(arg) {
        const { invoiceIds, sellerWorkspaceId, status, remarks } = arg;
        return {
          url: `hub/proxy/api/turnover-invoices/update-status/bulk`,
          method: 'PUT',
          body: {
            invoiceIds,
            status,
            remarks,
          },
          params: {
            sellerWorkspaceId,
          },
        };
      },
    }),
    updateTurnoverSchemeSortOrder: builder.mutation<
      UpdateTurnoverSchemeSortOrderResponse,
      UpdateTurnoverSchemeSortOrderRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, schemes } = arg;
        return {
          url: `hub/proxy/api/turnover-schemes/update/sort-order`,
          method: 'PUT',
          body: schemes,
          params: {
            sellerWorkspaceId,
          },
        };
      },
    }),
    getTurnoverInvoiceActivityLogs: builder.query<
      TurnoverInvoiceActivityLogResponse,
      GetTurnoverInvoiceActivityLogsRequest
    >({
      query(arg) {
        const { invoiceId, sellerWorkspaceId } = arg;
        return {
          url: `hub/proxy/api/turnover-invoices/${sellerWorkspaceId}/${invoiceId}/activity-logs?sellerWorkspaceId=${sellerWorkspaceId}`,
          method: 'GET',
        };
      },
    }),
    evaluateTurnoverInvoice: builder.mutation<
      EvaluateTurnoverInvoiceResponse,
      EvaluateTurnoverInvoiceRequest
    >({
      query(args) {
        return {
          url: `hub/proxy/api/turnover-invoices/evaluate`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
    remapCustomerProduct: builder.mutation<
      RemapCustomerProductResponse,
      RemapCustomerProductRequest
    >({
      query(args) {
        const { sellerWorkspaceId, invoiceId, ...body } = args;
        return {
          url: `hub/proxy/api/turnover-invoices/product-mapping/${sellerWorkspaceId}`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),
    bulkCreateBuyXGetYScheme: builder.mutation<
      BulkCreateBuyXGetYSchemeResponse,
      BulkCreateBuyXGetYSchemeRequest
    >({
      query(args) {
        return {
          url: `hub/commerce-v2/scheme/v2/buyXgetYfree/bulk/${args.params.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
    bulkCreateProductDiscountScheme: builder.mutation<
      BulkCreateProductDiscountSchemeRespone,
      BulkCreateProductDiscountSchemeRequest
    >({
      query(args) {
        return {
          url: `hub/commerce-v2/scheme/v2/productdiscount/bulk/${args.params.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
  }),
});

export const {
  useCreateBuyXGetYMutation,
  useCreateProductDiscountMutation,
  useLazyGetSchemeQuery,
  useGetAllSchemesMutation,
  useCreateTrailSchemeMutation,
  useUpdateProductSchemeMutation,
  useUpdateTurnoverInvoiceStatusMutation,
  useBulkUpdateTurnoverInvoiceStatusMutation,
  useUpdateTurnoverSchemeSortOrderMutation,
  useLazyGetTurnoverInvoiceActivityLogsQuery,
  useEvaluateTurnoverInvoiceMutation,
  useRemapCustomerProductMutation,
  useBulkCreateBuyXGetYSchemeMutation,
  useBulkCreateProductDiscountSchemeMutation,
  useLazyGetSchemeFiltersQuery,
} = schemesApi;
